import { Helmet } from "react-helmet-async";

const SITE_NAME = "Pondok Tahfizh Generasi Rabbani Qurani";
const DEFAULT_DESCRIPTION =
  "Pondok Tahfizh Generasi Rabbani Qurani — rumah bagi para penghafal Al-Qur'an dengan metode tahsin, tahfizh, dan mutqin yang terstruktur.";

function origin() {
  if (typeof window !== "undefined" && window.location && window.location.origin) {
    return window.location.origin;
  }
  return "";
}

/**
 * <Seo /> — declarative meta / OG / Twitter tags.
 *
 * Props:
 *  - title (string)         page-specific title (appended before site name)
 *  - description (string)   meta description + og:description
 *  - image (string)         absolute URL for og:image (recommended 1200x630)
 *  - url (string)           canonical URL (defaults to current href)
 *  - type (string)          og:type (default 'website'; use 'article' for detail)
 *  - published (string)     ISO date for article:published_time (optional)
 *  - modified (string)      ISO date for article:modified_time (optional)
 *  - author (string)        article:author (optional)
 *  - section (string)       article:section / category (optional)
 *  - tags (string[])        article:tag (optional, repeated)
 *  - siteName (string)      override site name (default = SITE_NAME)
 *  - noindex (bool)         emit robots noindex,nofollow when true
 */
export default function Seo({
  title,
  description = DEFAULT_DESCRIPTION,
  image,
  url,
  type = "website",
  published,
  modified,
  author,
  section,
  tags = [],
  siteName = SITE_NAME,
  noindex = false,
}) {
  const fullTitle = title ? `${title} · ${siteName}` : siteName;
  const canonical = url || (typeof window !== "undefined" ? window.location.href : "");
  const finalImage = image || `${origin()}/logo512.png`;

  return (
    <Helmet prioritizeSeoTags>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {noindex && <meta name="robots" content="noindex,nofollow" />}
      {canonical && <link rel="canonical" href={canonical} />}

      {/* Open Graph */}
      <meta property="og:site_name" content={siteName} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={type} />
      {canonical && <meta property="og:url" content={canonical} />}
      <meta property="og:image" content={finalImage} />
      <meta property="og:image:alt" content={title || siteName} />
      <meta property="og:locale" content="id_ID" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={finalImage} />

      {/* Article-specific */}
      {type === "article" && published && <meta property="article:published_time" content={published} />}
      {type === "article" && modified && <meta property="article:modified_time" content={modified} />}
      {type === "article" && author && <meta property="article:author" content={author} />}
      {type === "article" && section && <meta property="article:section" content={section} />}
      {type === "article" && tags.map((t) => (
        <meta key={t} property="article:tag" content={t} />
      ))}
    </Helmet>
  );
}
